// The middle-end resolver clamps its GraphQL `limit` to CONTENTFUL_MAX_LIMIT (100 — Contentful's
// per-collection maximum). Raising PAGE_SIZE above that would make the front-end stride `offset`
// further than a single response ever covers, silently skipping articles between pages.
export const PAGE_SIZE = 50;

// A deep-pagination ceiling, in the same spirit as Elasticsearch's 10,000-result `max_result_window`.
// It does three things, none of which a real visitor can reach at ~2-3 pages per category:
//   1. Without it, a large enough `?page=` overflows the 32-bit signed Int that GraphQL's `offset`
//      arg accepts. That surfaces as a pathless (no `path`) request-level error, which gets reported
//      and thrown as a plain Error — a 500 — instead of redirecting via the normal `page > totalPages`
//      check. Any crawler poking at `?page=` can trip it.
//   2. The middle-end's Contentful cache is keyed on the query variables and has no TTL or eviction,
//      and the out-of-range check happens after the fetch — so each distinct junk page number leaves
//      a permanent cache entry. The ceiling caps how many can ever exist.
//   3. The out-of-range check happens after the GraphQL fetch, so every junk page number is a real
//      Contentful round trip with `skip` up to (MAX_PAGE_NUMBER - 1) * PAGE_SIZE. Contentful's
//      per-collection `skip` ceiling isn't published, so this stays low enough to comfortably clear
//      it rather than risk exceeding it and turning into the same 500 this ceiling exists to avoid.
// At 100, that's 5,000 articles deep — still far beyond the 2-3 pages these categories realistically
// have. Anything above it clamps here and then redirects normally, so the visible behaviour is unchanged.
export const MAX_PAGE_NUMBER = 100;

export function parsePageParam(raw: string | undefined): number {
	if (raw === undefined || !/^\d+$/.test(raw)) return 1;

	const parsed = Number.parseInt(raw, 10);

	if (parsed < 1) return 1;
	if (parsed > MAX_PAGE_NUMBER) return MAX_PAGE_NUMBER;

	return parsed;
}
